import React, { useState, useEffect ,useMemo} from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  InputAdornment,
  Tooltip,
  Alert,
  Snackbar,
  Divider,
  Fab,
  Menu,
  Input,
  TablePagination,
  alpha,
  useTheme,
  Drawer,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Class as ClassIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  PersonAdd as PersonAddIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  MoreVert as MoreIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useNavigate } from "react-router-dom";
import AddStaffForm from "./AddStaff";
import axios from "axios";
import apiEndpoints from "../../../apiconfig";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import DeleteConfirmDialog from "../../DeleteConfirmDialog";
const StaffListing = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // --- STATE ---
  const [staff, setStaff] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const { showSnackbar } = useSnackbar();
  const [filterStatus, setFilterStatus] = useState("all");
  const [anchorEl, setAnchorEl] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteGuid, setDeleteGuid] = useState(null);
  const [filterRole, setFilterRole] = useState("All");    // new filter
  const [classOptions, setClassOptions] = useState([]);
useEffect(() => {
  const fetchClasses = async () => {
    const token = localStorage.getItem("token");
    const res = await axios.get(`${apiEndpoints.dropdown}?type=class`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.data?.status === "success") {
      setClassOptions(res.data.data);
    }
  };

  fetchClasses();
}, []);

  // legacy formData + dialog kept (you said don't remove functionality)
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    qualification: "",
    experience: "",
    specialization: [],
    assignedClass: "",
    joiningDate: new Date(),
    profilePhoto: "",
    status: true,
    role: "",
    department: "",
    office: "",
    bio: "",
    password: "",
    salary: "", // Added salary field
  });

  // --- MAPPER: API -> UI shape ---
  const mapApiToUi = (row) => {
    return {
      id: row.id,
      guid: row.guid,
      branch: row.branch_guid ?? null,
      firstName: row.first_name || "",
      lastName: row.last_name || "",
      email: row.email || "",
      phone: row.phone || row.mobile || "",
      // password is decrypted in PHP before sending
      password: row.password || "",
      qualification: row.qualification || "",
      experience: row.years_of_experience ?? 0,
      specialization: row.specialization || [],
      assignedClass: row.assigned_class || "",
      assignedClassName: row.assigned_class_name || "—",
      salary: row.salary || "", // Added salary field
      joiningDate: row.joining_date || "",
      profilePhoto: row.profile_photo || "",
      // map tinyint isActive -> text status for UI
      status:
        row.isActive === 1 || row.isActive === "1" ? "Active" : "Inactive",
      // these may not exist in DB yet; keep for UI compatibility
      role: row.role || "",
      department: row.department || "",
      office: row.office || "",
      bio: row.bio || "",
    };
  };

  // --- MAPPER: UI -> API payload ---
  const mapUiToApiPayload = (data, includePassword = true) => {
    const joiningDateValue =
      data.joiningDate instanceof Date
        ? data.joiningDate.toISOString().split("T")[0]
        : data.joiningDate || null;

    const payload = {
      branch: data.branch,
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      phone: data.phone,
      qualification: data.qualification,
      years_of_experience: Number(data.yearsOfExperience || 0),
      specialization: data.specialization || [],
      assigned_class: data.assignedClass || "",
      salary: parseFloat(data.salary) || 0.00, // Added salary field
      joining_date: joiningDateValue,
      profile_photo: data.profilePhoto || "",
      role: data.role || "",
    };

    if (includePassword && data.password) {
      payload.password = data.password;
    }

    return payload;
  };

  // --- FETCH ALL STAFF ---
  const fetchStaff = async () => {
    try {
      setLoading(true);
      const res = await axios.get(apiEndpoints.staff);
      if (res.data.status === "success") {
        const mapped = (res.data.data || []).map(mapApiToUi);
        setStaff(mapped);
      } else {
        showSnackbar("Failed to load staff records", "error")
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
      showSnackbar("Unable to load staff. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  // --- STATS ---
  const stats = {
    total: staff.length,
    active: staff.filter((s) => s.status === "Active").length,
    professors: staff.filter(
      (s) => s.role && s.role.toString().includes("Professor")
    ).length,
    assistants: staff.filter((s) => s.role === "Teaching Assistant").length,
  };

  // --- CRUD HANDLERS INTEGRATED WITH BACKEND ---

  const handleAdd = async (newStaffFromForm) => {
    try {
      const payload = mapUiToApiPayload(newStaffFromForm, true); // include password

      const res = await axios.post(apiEndpoints.staff, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (res.data.status === "success") {
        showSnackbar("Staff member created successfully", "success")
        await fetchStaff();
      } else {
        showSnackbar(
          showSnackbar("Failed to create staff member", "error"),
          "success"
        );
      }
    } catch (error) {
      console.error("Error adding staff:", error);
      showSnackbar("Error while creating staff member", "error")
    }
  };

  const handleUpdate = async (updatedStaffFromForm) => {
    console.log("Updating staff:", updatedStaffFromForm);
    if (!updatedStaffFromForm?.guid) {
      showSnackbar("Missing staff ID for update", "error")
      return;
    }

    try {
      const includePassword = !!updatedStaffFromForm.password;
      const payload = mapUiToApiPayload(
        updatedStaffFromForm,
        includePassword // only send password if present
      );

      const res = await axios.put(
        `${apiEndpoints.staff}?guid=${updatedStaffFromForm.guid}`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (res.data.status === "success") {
        showSnackbar("Staff member updated successfully", "success")
        await fetchStaff();
      } else {
        showSnackbar("Failed to update staff member", "error")

      }
    } catch (error) {
      console.error("Error updating staff:", error);
      showSnackbar("Error while updating staff member", "error");
    }
  };

  const handleDelete = async (guid) => {

    try {
      const res = await axios.delete(`${apiEndpoints.staff}?guid=${guid}`);

      if (res.data.status === "success") {
        showSnackbar("Staff member deleted successfully", "success");

        await fetchStaff();
      } else {
        showSnackbar("Failed to delete staff member", "error");
      }
    } catch (error) {
      console.error("Error deleting staff:", error);
      showSnackbar("Error while deleting staff member", "error");
    } finally {
      setDeleteDialogOpen(false);
      setDeleteGuid(null);
    }
  };


  const handleViewStaff = (guid) => {
    navigate(`/admin/staff/${guid}`);
  };

  const handleSortMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({ ...prev, profilePhoto: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

const filteredStaff = useMemo(() => {
  return staff.filter((member) => {
    if (filterRole !== "All" && member.role !== filterRole) return false;

    if (!searchTerm) return true;

    const searchableFields = [
      member.firstName,
      member.lastName,
      member.email,
      member.role,
      member.department,
      member.qualification,
      ...(member.specialization || []),
      member.assignedClassName,
    ];

    return searchableFields.some((field) =>
      String(field || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
  });
}, [staff, filterRole, searchTerm]);


  const StatusBadge = ({ status }) => (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "0.7rem",
        fontWeight: 600,
        background:
          status === "Active"
            ? alpha(theme.palette.success.main, 0.15)
            : alpha(theme.palette.error.main, 0.15),
        color:
          status === "Active"
            ? theme.palette.success.main
            : theme.palette.error.main,
      }}
    >
      {status === "Active" ? (
        <CheckCircleIcon sx={{ fontSize: 12, mr: 0.5 }} />
      ) : (
        <CancelIcon sx={{ fontSize: 12, mr: 0.5 }} />
      )}
      {status}
    </Box>
  );

const availableRoles = useMemo(() => {
  const set = new Set();
  staff.forEach((s) => {
    if (s.role) set.add(s.role);
  });
  return ["All", ...Array.from(set)];
}, [staff]);

const cardStyle = {
  flex:1,
  p:{ xs:1.5, sm:2 },
  borderRadius:"14px",
  minHeight:{ xs:90, sm:100, md:120, lg:130 },
  display:"flex",
  justifyContent:"space-between",
  alignItems:"center",
  background:"rgba(255,255,255,0.58)",
  border:"1px solid rgba(255,255,255,0.5)",
  backdropFilter:"blur(16px)",
  boxShadow:"0 8px 22px rgba(0,0,0,.12)",
};

const iconBox = {
  width:42, height:42,
  borderRadius:"12px",
  display:"flex",
  justifyContent:"center",
  alignItems:"center",
  background:"rgba(0,0,0,0.06)"
};

const iconSize = { fontSize:22 };

const countStyle = {
  fontSize:{ xs:"1rem", sm:"1.2rem" },
  fontWeight:700
};

const labelStyle = {
  fontSize:{ xs:"0.75rem", sm:"0.85rem" },
  color:"text.secondary"
};


  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          p: 3,
          minHeight: "100vh",
          position: "relative",
          overflow: "hidden",
          background: "rgba(255, 255, 255, 0.25)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.4)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: `
                radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)
              `,
            pointerEvents: "none",
          },
        }}
      >
        <Container
          maxWidth="100%"
        sx={{ fontFamily: "Montserrat, sans-serif", minHeight: "100vh" }}
        >
          {/* Header */}
          <Box
            sx={{
              mb: 4,
              position: "relative",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* LEFT SIDE: Title + Subtitle */}
            <Box>
              <Typography variant="h4" fontWeight="700" gutterBottom>
                Staff Directory
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage all faculty and staff members
              </Typography>
            </Box>

            {/* RIGHT SIDE: FAB BUTTON */}
            <Fab
              color="primary"
              size="medium"
              sx={{
                boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
                flexShrink: 0,
                width: { xs: 48, sm: 56, md: 56 },
                height: { xs: 48, sm: 56, md: 56 },
                "& .MuiSvgIcon-root": {
                  fontSize: { xs: "1.2rem", sm: "1.4rem", md: "1.6rem" },
                },
                mr: { xs: 1, sm: 0 },
              }}
              onClick={() => {
                // open your Add Staff form here
                setEditingStaff(null);
                setOpenForm(true);
              }}
            >
              <AddIcon />
            </Fab>
          </Box>

          {/* Stats Cards */}
          {/* ================= STAT CARDS FINAL RESPONSIVE ================= */}
          <Grid
            container
            spacing={2}
            sx={{
              mb: 3,
              justifyContent: "space-between",
            }}
          >
            {/* Total Staff */}
            <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
              <Paper sx={cardStyle}>
                <Box>
                  <Typography sx={countStyle} color="primary">
                    {stats.total}
                  </Typography>
                  <Typography sx={labelStyle}>Total Staff</Typography>
                </Box>
                <Box sx={iconBox}>
                  <PersonAddIcon sx={iconSize} />
                </Box>
              </Paper>
            </Grid>

            {/* Active */}
            <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
              <Paper sx={cardStyle}>
                <Box>
                  <Typography sx={countStyle} color="success.main">
                    {stats.active}
                  </Typography>
                  <Typography sx={labelStyle}>Active</Typography>
                </Box>
                <Box sx={iconBox}>
                  <CheckCircleIcon
                    sx={{ ...iconSize, color: "success.main" }}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Professors */}
            <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
              <Paper sx={cardStyle}>
                <Box>
                  <Typography sx={countStyle} color="warning.main">
                    {stats.professors}
                  </Typography>
                  <Typography sx={labelStyle}>Professors</Typography>
                </Box>
                <Box sx={iconBox}>
                  <SchoolIcon sx={{ ...iconSize, color: "warning.main" }} />
                </Box>
              </Paper>
            </Grid>

            {/* Assistants */}
            <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
              <Paper sx={cardStyle}>
                <Box>
                  <Typography sx={countStyle} color="info.main">
                    {stats.assistants}
                  </Typography>
                  <Typography sx={labelStyle}>Assistants</Typography>
                </Box>
                <Box sx={iconBox}>
                  <WorkIcon sx={{ ...iconSize, color: "info.main" }} />
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Control Bar */}
          {/* Control Bar */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 3,
              width: "100%",
            }}
          >
            {/* SEARCH BAR */}
            <TextField
              placeholder="Search staff..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              sx={{
                flex: 1,
                bgcolor: "rgba(255,255,255,0.65)",
                borderRadius: "50px",
                height: "46px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                  height: "46px",
                },
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& input": { paddingLeft: "10px" },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ opacity: 0.7 }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* FILTER DROPDOWN (same size as search) */}
            <FormControl
              size="small"
              sx={{
                width: { xs: "100%", md: "260px" }, // adjust width here if needed
                bgcolor: "rgba(255,255,255,0.65)",
                borderRadius: "50px",
              }}
            >
              <InputLabel>Filter by Role</InputLabel>
              <Select
                label="Filter by Role"
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                sx={{
                  borderRadius: "50px",
                  height: "46px",
                  display: "flex",
                  alignItems: "center",
                  "& fieldset": { border: "none" },
                }}
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Loading indicator on first load */}
          {loading && staff.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 6,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Staff Grid */}
              <Grid container spacing={3}>
                {filteredStaff
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((staffMember) => (
                    <Grid
                      item
                      xs={12} // full width mobile
                      sm={6} // 2 per row from 600px+
                      md={6} // 2 per row at 768px - 1200px (fix for 1024px)
                      lg={4} // 3 per row only on large desktop
                      xl={3} // 4 per row on very large screens
                      key={staffMember.guid}
                      sx={{ display: "flex" }}
                    >
                      <Card
                        sx={{
                          borderRadius: "14px",
                          p: { xs: 2, sm: 2.5, md: 3 },
                          minHeight: {
                            xs: 130,
                            sm: 150,
                            md: 160,
                            lg: 170,
                          },
                          height: "auto",

                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: { xs: 1.2, sm: 1.5, md: 2 },
                          background: "rgba(255, 255, 255, 0.5)",
                          backdropFilter: "blur(16px)",
                          border: "1px solid rgba(255,255,255,0.5)",
                          boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                          overflow: "hidden",
                          flex: 1,
                        }}
                      >
                        {/* LEFT SECTION */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 1.2, sm: 1.8 },
                            minWidth: 0,
                            flexWrap: { xs: "wrap", sm: "nowrap" },
                          }}
                        >
                          {/* Avatar */}
                          <Avatar
                            src={staffMember.profilePhoto}
                            sx={{
                              width: { xs: 50, sm: 60, md: 70 },
                              height: { xs: 50, sm: 60, md: 70 },
                              flexShrink: 0,
                            }}
                          />

                          {/* Staff Text */}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                textTransform: "uppercase",
                                fontWeight: 700,
                                fontSize: { xs: "0.95rem", sm: "1rem" },
                                mb: 0.4,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: { xs: 120, sm: 160, md: 200 },
                              }}
                            >
                              {staffMember.firstName} {staffMember.lastName}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                                color: "text.secondary",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: { xs: 120, sm: 160, md: 200 },
                              }}
                            >
                              {staffMember.role || "—"}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                                color: "text.secondary",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: { xs: 120, sm: 160, md: 200 },
                              }}
                            >
                              {staffMember.assignedClassName}
                            </Typography>
                          </Box>
                        </Box>

                        {/* RIGHT SECTION – ACTION BUTTONS */}
                        {/* ACTION BUTTONS – VERTICAL LIKE STUDENT PAGE */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column", // vertical alignment
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1.2,
                            minWidth: "40px", // keeps vertical block aligned right
                            pr: 1,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleViewStaff(staffMember.guid)}
                            sx={{
                              bgcolor: "rgba(0,0,0,0.05)",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.1)" },
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingStaff(staffMember);
                              setOpenForm(true);
                            }}
                            sx={{
                              bgcolor: "rgba(0,0,0,0.05)",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.1)" },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>

                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setDeleteGuid(staffMember.guid);
                              setDeleteDialogOpen(true);
                            }}
                            sx={{
                              bgcolor: "rgba(255,0,0,0.08)",
                              "&:hover": { bgcolor: "rgba(255,0,0,0.15)" },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
              </Grid>

              {/* Pagination */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: {
                    xs: "center", // mobile
                    sm: "flex-end", // from 600px → including 768px
                    md: "flex-end", // desktop
                  },
                  mt: 4,
                }}
              >
                <TablePagination
                  component="div"
                  count={filteredStaff.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[9, 18, 36]}
                  sx={{
                    width: "100%",
                    ".MuiTablePagination-toolbar": {
                      justifyContent: "space-between",
                    },
                  }}
                />
              </Box>
            </>
          )}

          {/* Drawer with Add/Edit Form */}
          {openForm && (
            <AddStaffForm
              mode={editingStaff ? "edit" : "add"}
              initialData={editingStaff}
              onSubmit={async (data) => {
                if (editingStaff) await handleUpdate(data);
                else await handleAdd(data);
                setOpenForm(false);
              }}
              onClose={() => {
                setOpenForm(false);
                setEditingStaff(null);
              }}
            />
          )}
        </Container>
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          title="Delete Staff Member"
          message="Are you sure you want to delete this staff member? This action cannot be undone."
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={() => handleDelete(deleteGuid)}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default StaffListing;